import { useState } from "react";
import styled from "styled-components";
import { useAppStore } from "@/stores/appStore";
import { type_d } from "@/theme/typography";

interface ItemType {
	id: string;
	title: string;
	icon?: React.ReactNode;
}

interface ShapesProps<T extends ItemType> {
	items: T[];
	stateKey: string;
	defaultVal: T;
}

export default function Shapes<T extends ItemType>({ items, stateKey, defaultVal }: ShapesProps<T>) {
	const [active, setActive] = useState<T>(defaultVal);

	const updateState = (value: T) => {
		setActive(value);

		useAppStore.setState((prevState) => ({
			configurator: {
				...prevState.configurator,
				[stateKey]: {
					...prevState.configurator[stateKey], // Preserve existing values
					...value,
				},
			},
		}));
	};

	return (
		<ShapeWrapper>
			{items &&
				items.map((item, index: number) => (
					<div
						key={index}
						onClick={() => updateState(item)}
						className={`shape__item ${active.id == item.id && "is_active"}`}>
						{item.icon && item.icon()}
						<p className="shape__item__title" style={type_d}>
							{item.title}
						</p>
					</div>
				))}
		</ShapeWrapper>
	);
}

const ShapeWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minMax(0, 1fr));
	row-gap: 0.5rem;
	column-gap: 8px;

	& > * {
		width: 100%;
		background: #e9e1de;
		padding-inline: 16px;
		padding-block: 0.8rem;
		border-radius: 5px;
	}

	.shape__item {
		display: flex;
		align-items: center;
		column-gap: 1rem;
		transition: background-color 98ms linear;
		cursor: pointer;

		svg {
			width: 20px;
			fill: #7d6a64;
		}
	}

	.shape__item:hover {
		background-color: ${({ theme }) => theme.colors.secondary};
		color: #ffe6dc;
		transition: color 98ms linear;

		svg {
			fill: #ffe6dc;
		}

		.shape__item__title {
			color: #ffe6dc;
		}
	}

	.shape__item.is_active {
		background-color: ${({ theme }) => theme.colors.secondary};
		svg {
			fill: #ffe6dc;
		}
	}

	.shape__item__title {
		font-weight: 400;
		color: #7d6a64;
	}

	.shape__item.is_active .shape__item__title {
		font-weight: 400;
		color: #ffe6dc;
	}

	.shape__item svg.icon__x-mark {
		width: 15px;
	}
`;
